import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { rememberDocNumbers } from '../utils/docNumberLookup';
import {
  getTransactions,
  getTransactionsPage,
  getTransactionsCount,
  getTransactionById,
  getDocListSummary
} from '../utils/transactionHelper';

/**
 * useDocListPagination — paginated document lists (2026-07-16, Firebase cost).
 *
 * Default browse view: 20 docs/page straight from Firestore (~21 reads per
 * form open instead of the tranType's full history). The moment the user
 * searches, filters by status, or applies a custom column sort, the hook
 * switches to FULL mode — one full active-year load via getTransactions
 * (exactly what every open used to cost) — so search/filter/sort behavior is
 * IDENTICAL to before, across the whole year, any column.
 *
 * Drop-in contract for the form pages:
 *   const { docs: invoices, refresh: loadInvoices, ensureDoc, pagination } =
 *     useDocListPagination({ tranTypeCode: 'SI', fullMode: isFiltering });
 * `loadInvoices()` keeps working at every existing call site (post-save
 * reloads, effects) — it reloads page 1 + the count, or the full list when in
 * full mode. Render <DocListPagination {...pagination} /> under the table.
 */

const toYMD = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (v.toDate) { // Firestore Timestamp
    const d = v.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  if (v instanceof Date) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  return null;
};

/**
 * @param summarySpec optional — { statuses: ['Draft','Posted','Paid'],
 *   outstandingStatuses: ['Posted','Partial'], outstandingField: 'balanceAmount' }
 *   Feeds the footer SummaryBar: aggregated server-side in paged mode,
 *   computed from the loaded full list in full mode (identical numbers).
 */
const useDocListPagination = ({ tranTypeCode, pageSize = 20, fullMode = false, summarySpec = null }) => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const companyId = activeCompany?.id || null;
  const branchId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
  const fromDate = toYMD(activeFinancialYear?.startDate);
  const toDate = toYMD(activeFinancialYear?.endDate);

  const [docs, setDocs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ counts: {}, outstanding: 0 });

  // Cursor per visited page: cursors.current[n] = lastVisible of page n.
  const cursors = useRef({});
  // Full-year list, loaded once per mount when fullMode first activates,
  // cleared on refresh so it never goes stale after a save.
  const fullList = useRef(null);
  // Extra docs fetched by id (report drill-down) so openers can find them.
  const [extraDocs, setExtraDocs] = useState([]);

  const criteria = useMemo(
    () => ({ tranTypeCode, branchId, fromDate, toDate, pageSize }),
    [tranTypeCode, branchId, fromDate, toDate, pageSize]
  );

  const loadPage = useCallback(async (targetPage) => {
    if (!companyId || !tranTypeCode) return;
    setLoading(true);
    try {
      // Walk forward from the nearest cached cursor when jumping ahead.
      let start = targetPage;
      while (start > 1 && !cursors.current[start - 1]) start -= 1;
      let cursor = start > 1 ? cursors.current[start - 1] : null;
      let result = null;
      for (let p = start; p <= targetPage; p += 1) {
        result = await getTransactionsPage(companyId, { ...criteria, cursor });
        cursor = result.lastVisible;
        if (result.lastVisible) cursors.current[p] = result.lastVisible;
        if (!result.hasMore && p < targetPage) break; // ran out of docs early
      }
      setDocs(result ? result.rows : []);
      setPage(targetPage);
    } catch (error) {
      console.error('[useDocListPagination] page load failed:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, tranTypeCode, criteria]);

  const loadCount = useCallback(async () => {
    if (!companyId || !tranTypeCode) return;
    try {
      setTotalCount(await getTransactionsCount(companyId, criteria));
    } catch (error) {
      console.error('[useDocListPagination] count failed:', error);
    }
  }, [companyId, tranTypeCode, criteria]);

  const summarizeLocal = useCallback((rows) => {
    if (!summarySpec) return;
    const counts = {};
    (summarySpec.statuses || []).forEach(s => {
      counts[s] = rows.filter(r => r.status === s).length;
    });
    const outSet = new Set(summarySpec.outstandingStatuses || []);
    const field = summarySpec.outstandingField || 'balanceAmount';
    const outstanding = outSet.size
      ? rows.filter(r => outSet.has(r.status)).reduce((s, r) => s + (r[field] || 0), 0)
      : 0;
    setSummary({ counts, outstanding });
  }, [summarySpec]);

  const loadSummary = useCallback(async () => {
    if (!companyId || !tranTypeCode || !summarySpec) return;
    try {
      setSummary(await getDocListSummary(companyId, { ...criteria, ...summarySpec }));
    } catch (error) {
      console.error('[useDocListPagination] summary failed:', error);
    }
  }, [companyId, tranTypeCode, criteria, summarySpec]);

  const loadFull = useCallback(async () => {
    if (!companyId || !tranTypeCode) return;
    setLoading(true);
    try {
      if (!fullList.current) {
        // COST: fromDate/toDate are the ACTIVE YEAR's window — the same bounds
        // the paged path already uses (see `criteria`). Without them Firestore
        // returned this tranType's ENTIRE multi-year history on the first
        // keystroke / status pick / column-header click (3,000-15,000 docs),
        // and `activeYearOnly` then threw most of it away in the browser.
        // Sending the window makes search/sort match what the paged list shows.
        // activeYearOnly stays on: it keeps legacy rows that were never tagged
        // with a financialYearId but do fall inside the date window.
        fullList.current = await getTransactions(companyId, {
          tranTypeCode,
          activeYearOnly: true,
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(branchId ? { branchId } : {})
        });
      }
      setDocs(fullList.current);
      summarizeLocal(fullList.current);
    } catch (error) {
      console.error('[useDocListPagination] full load failed:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, tranTypeCode, branchId, fromDate, toDate, summarizeLocal]);

  // refresh() — same name/shape the forms already call after save/delete.
  const refresh = useCallback(async () => {
    cursors.current = {};
    fullList.current = null;
    setExtraDocs([]);
    if (fullMode) {
      await Promise.all([loadFull(), loadCount()]);
    } else {
      await Promise.all([loadPage(1), loadCount(), loadSummary()]);
    }
  }, [fullMode, loadFull, loadPage, loadCount, loadSummary]);

  // Initial load + reload when company/FY/branch or mode changes.
  useEffect(() => {
    cursors.current = {};
    if (fullMode) {
      loadFull();
      loadCount();
    } else {
      loadPage(1);
      loadCount();
      loadSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, tranTypeCode, branchId, fromDate, toDate, fullMode]);

  // Optimistic local update after save — the background refresh() reconciles.
  const applyLocal = useCallback((updater) => {
    setDocs(prev => updater(prev));
    if (fullList.current) {
      try { fullList.current = updater(fullList.current); } catch { /* refresh will fix */ }
    }
  }, []);

  const goToPage = useCallback((p) => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const target = Math.min(Math.max(1, p), totalPages);
    loadPage(target);
  }, [loadPage, totalCount, pageSize]);

  // Report drill-down: make sure a specific doc is present in `docs`.
  const ensureDoc = useCallback(async (transactionId) => {
    const inList = docs.find(d => d.id === transactionId)
      || extraDocs.find(d => d.id === transactionId);
    if (inList) return inList;
    if (!companyId) return null;
    try {
      const fetched = await getTransactionById(companyId, transactionId);
      if (fetched && fetched.tranTypeCode === tranTypeCode) {
        setExtraDocs(prev => [fetched, ...prev.filter(d => d.id !== fetched.id)]);
        return fetched;
      }
    } catch (error) {
      console.error('[useDocListPagination] ensureDoc failed:', error);
    }
    return null;
  }, [docs, extraDocs, companyId, tranTypeCode]);

  // Docs exposed to the page: current page (plus drill-down extras) or full list.
  const allDocs = useMemo(() => {
    if (fullMode) return docs;
    if (!extraDocs.length) return docs;
    const ids = new Set(docs.map(d => d.id));
    return [...extraDocs.filter(d => !ids.has(d.id)), ...docs];
  }, [docs, extraDocs, fullMode]);

  // Linked-document numbers: every loaded list teaches them for free, so other
  // screens can show "SO-2026-00012" without reading it again.
  useEffect(() => { rememberDocNumbers(companyId, allDocs); }, [companyId, allDocs]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return {
    docs: allDocs,
    loading,
    refresh,
    ensureDoc,
    applyLocal,
    summary,
    pagination: {
      page,
      totalPages,
      totalCount,
      goToPage,
      visible: !fullMode && totalPages > 1
    }
  };
};

export default useDocListPagination;
